import { createGlobalStyle } from 'styled-components';

// Theme definition
export const lightTheme = {
  // Main colors
  primary: '#3F8CFF', // Bright cartoon blue
  secondary: '#00E5FF', // Light cyan blue
  accent: '#FFA726', // Warm orange
  
  // Background colors
  background: '#E8F5FF', // Very light blue background
  backgroundAlt: '#C9E8FF', // Slightly darker blue for contrast areas
  backgroundLight: '#ADDBFF', // Medium light blue
  
  // Text colors
  text: '#0B3866', // Deep blue for text
  textSecondary: '#1A5D9F', // Medium blue for secondary text
  textMuted: '#3787D6', // Light blue for muted text
  
  // Border colors
  border: '#80BDFF', // Medium blue border
  borderLight: '#B6D8FF', // Light blue border
  
  // Status colors
  success: '#00C853', // Bright green
  error: '#FF5252', // Bright red
  warning: '#FFB300', // Bright amber
  info: '#29B6F6', // Light blue
  
  // Shadows
  shadowLight: '0 4px 10px rgba(63, 140, 255, 0.15)',
  shadowMedium: '0 8px 20px rgba(63, 140, 255, 0.2)',
  shadowStrong: '0 12px 40px rgba(63, 140, 255, 0.25)',
};

// Dark theme variation
export const darkTheme = {
  // Main colors
  primary: '#4F97FF', // Slightly brighter blue for dark mode
  secondary: '#33E0FF', // Brighter cyan for dark mode
  accent: '#FFB74D', // Brighter orange for dark mode
  
  // Background colors
  background: '#0A1929', // Dark blue background
  backgroundAlt: '#132F4C', // Darker blue for contrast areas
  backgroundLight: '#1E4976', // Medium dark blue
  
  // Text colors
  text: '#E3F2FD', // Light blue for text
  textSecondary: '#90CAF9', // Medium light blue for secondary text
  textMuted: '#64B5F6', // Light blue for muted text
  
  // Border colors
  border: '#2979FF', // Medium blue border
  borderLight: '#5393FF', // Light blue border
  
  // Status colors
  success: '#00E676', // Bright green
  error: '#FF5252', // Bright red
  warning: '#FFD54F', // Bright amber
  info: '#40C4FF', // Light blue
  
  // Shadows
  shadowLight: '0 4px 10px rgba(0, 0, 0, 0.3)',
  shadowMedium: '0 8px 20px rgba(0, 0, 0, 0.5)',
  shadowStrong: '0 12px 40px rgba(0, 0, 0, 0.7)',
};

// Default theme (for backward compatibility)
export const theme = lightTheme;

// Keyframe animations
const keyframes = {
  pulse: `
    @keyframes pulse {
      0% { opacity: 1; }
      50% { opacity: 0.6; }
      100% { opacity: 1; }
    }
  `,
  breathe: `
    @keyframes breathe {
      0% { transform: scale(1); }
      50% { transform: scale(1.04); }
      100% { transform: scale(1); }
    }
  `,
  float: `
    @keyframes float {
      0% { transform: translateY(0px); }
      50% { transform: translateY(-10px); }
      100% { transform: translateY(0px); }
    }
  `,
  glow: `
    @keyframes glow {
      0% { box-shadow: 0 0 5px rgba(13, 71, 161, 0.3); }
      50% { box-shadow: 0 0 20px rgba(13, 71, 161, 0.6); }
      100% { box-shadow: 0 0 5px rgba(13, 71, 161, 0.3); }
    }
  `,
  bounce: `
    @keyframes bounce {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-10px); }
    }
  `,
  wiggle: `
    @keyframes wiggle {
      0%, 100% { transform: rotate(-3deg); }
      50% { transform: rotate(3deg); }
    }
  `,
  gradient: `
    @keyframes gradient {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
  `
};

export const GlobalStyle = createGlobalStyle`
  ${keyframes.pulse}
  ${keyframes.breathe}
  ${keyframes.float}
  ${keyframes.glow}
  ${keyframes.bounce}
  ${keyframes.wiggle}
  ${keyframes.gradient}

  @import url('https://fonts.googleapis.com/css2?family=Fredoka:wght@400;500;600;700&display=swap');
  @import url('https://fonts.googleapis.com/css2?family=Quicksand:wght@400;500;600;700&display=swap');
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500&display=swap');

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  }

  :root {
    color-scheme: ${props => props.theme === darkTheme ? 'dark' : 'light'};
    --primary: ${props => props.theme.primary};
    --secondary: ${props => props.theme.secondary};
    --accent: ${props => props.theme.accent};
  }

  body {
    font-family: 'Quicksand', sans-serif;
    background: ${props => props.theme.background};
    color: ${props => props.theme.text};
    line-height: 1.6;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    overflow-x: hidden;
    min-height: 100vh;
    position: relative;
    
    &::before {
      content: '';
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: 
        linear-gradient(125deg, 
          ${props => props.theme.primary} 0%, 
          ${props => props.theme.secondary} 50%, 
          ${props => props.theme.accent} 100%);
      opacity: ${props => props.theme === darkTheme ? '0.05' : '0.08'};
      z-index: -1;
      animation: gradient 15s ease infinite;
      background-size: 200% 200%;
    }
  }

  /* Custom scrollbar */
  ::-webkit-scrollbar {
    width: 10px;
  }

  ::-webkit-scrollbar-track {
    background: ${props => props.theme === darkTheme ? 
      'rgba(10, 25, 41, 0.7)' : 
      'rgba(201, 232, 255, 0.7)'};
  }

  ::-webkit-scrollbar-thumb {
    background: linear-gradient(to bottom, 
      rgba(${props => props.theme === darkTheme ? '79, 195, 247' : '63, 140, 255'}, 0.6), 
      rgba(${props => props.theme === darkTheme ? '41, 182, 246' : '0, 229, 255'}, 0.6));
    border-radius: 20px;
    
    &:hover {
      background: linear-gradient(to bottom, 
        rgba(${props => props.theme === darkTheme ? '79, 195, 247' : '63, 140, 255'}, 0.8), 
        rgba(${props => props.theme === darkTheme ? '41, 182, 246' : '0, 229, 255'}, 0.8));
    }
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: 'Fredoka', sans-serif;
    font-weight: 700;
    line-height: 1.25;
    margin-bottom: 0.5rem;
    color: ${props => props.theme.text};
    letter-spacing: -0.02em;
  }

  h1 {
    font-size: 2.5rem;
  }

  h2 {
    font-size: 2rem;
  }

  h3 {
    font-size: 1.5rem;
  }

  p {
    margin-bottom: 1rem;
    letter-spacing: 0.01em;
  }

  a {
    color: ${props => props.theme.primary};
    text-decoration: none;
    position: relative;
    transition: color 0.2s ease;
    font-weight: 600;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      width: 0;
      height: 3px;
      background: linear-gradient(90deg, ${props => props.theme.primary}, ${props => props.theme.secondary});
      transition: width 0.3s ease;
      border-radius: 3px;
    }
    
    &:hover {
      color: ${props => props.theme.secondary};
      
      &::after {
        width: 100%;
      }
    }
  }

  button, .button {
    padding: 0.75rem 1.25rem;
    background: ${props => props.primary ? 
      `linear-gradient(135deg, ${props.theme.primary}, ${props.theme.secondary})` : 
      'rgba(255, 255, 255, 0.8)'};
    color: ${props => props.primary ? '#fff' : props.theme.primary};
    border: 2px solid ${props => props.primary ? 'transparent' : props.theme.border};
    border-radius: 30px;
    font-family: 'Quicksand', sans-serif;
    font-weight: 600;
    font-size: 0.95rem;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: ${props => props.primary ? 
      `0 4px 15px rgba(63, 140, 255, 0.3)` : 
      '0 4px 10px rgba(0, 0, 0, 0.05)'};
    outline: none;
    
    &:hover, &:focus {
      transform: translateY(-3px);
      box-shadow: ${props => props.primary ? 
        `0 7px 20px rgba(63, 140, 255, 0.4)` : 
        '0 7px 15px rgba(0, 0, 0, 0.08)'};
    }
    
    &:active {
      transform: translateY(-1px);
      box-shadow: ${props => props.primary ? 
        `0 3px 10px rgba(63, 140, 255, 0.4)` : 
        '0 3px 8px rgba(0, 0, 0, 0.1)'};
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  }

  input, textarea, select {
    font-family: 'Quicksand', sans-serif;
    font-size: 0.95rem;
    background: rgba(255, 255, 255, 0.9);
    border: 2px solid ${props => props.theme.border};
    border-radius: 12px;
    color: ${props => props.theme.text};
    padding: 0.75rem 1rem;
    transition: all 0.3s ease;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    
    &:focus {
      outline: none;
      border-color: ${props => props.theme.primary};
      box-shadow: 0 0 0 3px rgba(13, 71, 161, 0.2);
    }
    
    &::placeholder {
      color: ${props => props.theme.textMuted};
      opacity: 0.7;
    }
  }

  code, pre {
    font-family: 'JetBrains Mono', monospace;
    background: rgba(13, 43, 69, 0.5);
    padding: 0.2em 0.4em;
    border-radius: 4px;
    font-size: 0.9em;
    border: 1px solid ${props => props.theme.border};
  }

  pre {
    padding: 1rem;
    overflow-x: auto;
    margin-bottom: 1.5rem;
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
  }

  ::selection {
    background: rgba(13, 71, 161, 0.3);
    color: ${props => props.theme.text};
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }

  .highlight {
    color: ${props => props.theme.primary};
    font-weight: 700;
  }

  .container {
    width: 100%;
    max-width: 1440px;
    margin: 0 auto;
    padding: 0 2rem;
    
    @media (max-width: 768px) {
      padding: 0 1rem;
    }
  }

  .card {
    background: rgba(31, 26, 54, 0.7);
    backdrop-filter: blur(10px);
    border-radius: 16px;
    padding: 2rem;
    border: 1px solid ${props => props.theme.border};
    box-shadow: ${props => props.theme.shadowMedium};
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    
    &:hover {
      transform: translateY(-5px);
      box-shadow: ${props => props.theme.shadowStrong};
    }
  }
  
  .glass {
    background: rgba(13, 71, 161, 0.1);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
  }
  
  .gradient-text {
    background: linear-gradient(90deg, ${props => props.theme.primary}, ${props => props.theme.accent});
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    color: transparent;
  }
  
  .glow {
    animation: ${keyframes.glow} 3s infinite;
  }
  
  .float {
    animation: ${keyframes.float} 5s ease-in-out infinite;
  }

  .bounce {
    animation: ${keyframes.bounce} 2s infinite ease-in-out;
  }
  
  .wiggle {
    animation: ${keyframes.wiggle} 2s infinite ease-in-out;
  }
`; 